import type { CSSProperties } from 'react';
import clsx from 'clsx';
import { EmblemAnimated, Flex, Text, useReducedMotion } from '@nula/core-ui';
import { useTemplate } from '@/lib/templates/use-templates-store';
import { joinTitles } from '@/lib/templates/join-titles';
import type { SurfaceEntry } from '@/lib/sync/document-surfaces';
import { SurfaceStatusLine } from './surface-status-line';
import styles from './surface-generating-body.module.css';

const SECTION_LINE_WIDTHS = [['94%', '81%', '66%'], ['90%', '58%'], ['72%']];

function SkeletonLines({ widths, pulse }: { widths: string[]; pulse: boolean }) {
  return widths.map((width, i) => (
    <Flex
      key={i}
      data-testid="generating-line"
      className={clsx(styles.line, pulse && styles.pulse)}
      style={{ '--line-width': width } as CSSProperties}
    />
  ));
}

export function SurfaceGeneratingBody({
  entry, sourceTitles,
}: { entry: SurfaceEntry; sourceTitles: readonly string[] }) {
  const reducedMotion = useReducedMotion();
  const template = useTemplate(entry.generator);
  const outputType = entry.surface === 'canvas' ? 'canvas' : 'text';
  const verb = outputType === 'canvas' ? 'Drawing' : 'Writing';
  const subject = template ? ` a ${template.name.toLowerCase()}` : '';
  const text =
    (entry.attempt ?? 0) >= 2
      ? 'Taking longer than usual. Trying again…'
      : `${verb}${subject} from ${joinTitles(sourceTitles, 'and')}…`;

  if (outputType === 'canvas') {
    return (
      <Flex vertical className={styles.column}>
        <SurfaceStatusLine text={text} />
        <Flex data-testid="generating-canvas-frame" className={styles.canvasFrame}>
          <EmblemAnimated size={64} color="var(--primary)" loop={!reducedMotion} />
        </Flex>
      </Flex>
    );
  }

  const sections = (template?.sections ?? []).filter((section) => section.required);
  return (
    <Flex vertical className={styles.column}>
      <SurfaceStatusLine text={text} />
      {sections.length > 0 ? (
        sections.map((section, i) => (
          <Flex vertical key={section.key} className={styles.section}>
            <Text size="lg" type="secondary" strong className={styles.heading}>{section.heading}</Text>
            <SkeletonLines widths={SECTION_LINE_WIDTHS[i % SECTION_LINE_WIDTHS.length]} pulse={!reducedMotion} />
          </Flex>
        ))
      ) : (
        <Flex vertical className={styles.section}>
          <SkeletonLines widths={SECTION_LINE_WIDTHS[0]} pulse={!reducedMotion} />
        </Flex>
      )}
    </Flex>
  );
}
